import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  createAdminVilla,
  deleteAdminVilla,
  getAdminVillas,
  updateAdminVilla,
  verifyAdminPassword,
} from '../../services/api';
import {
  FaBook,
  FaCar,
  FaChargingStation,
  FaCheck,
  FaChild,
  FaCube,
  FaDumbbell,
  FaLeaf,
  FaLightbulb,
  FaLocationDot,
  FaMagnifyingGlass,
  FaMapLocationDot,
  FaPersonWalking,
  FaRoad,
  FaShoePrints,
  FaShieldHalved,
  FaSun,
  FaTree,
  FaUsers,
  FaVideo,
  FaXmark,
} from 'react-icons/fa6';

const WIZARD_STEPS = [
  { id: 'basic', title: 'Basic Data', helper: 'Identity, logo, location, and banner image' },
  { id: 'detailed', title: 'Detailed Info', helper: 'Status, brochure, description, charges, and video' },
  { id: 'gallery', title: 'Gallery', helper: 'Exterior and interior images' },
  { id: 'highlights', title: 'Project Highlights', helper: 'Short selling points' },
  { id: 'details', title: 'Project Details', helper: 'Structured specs for the public page' },
  { id: 'amenities', title: 'Amenities & Features', helper: 'Custom amenity rows' },
  { id: 'availability', title: 'Availability Chart', helper: 'PDF upload' },
  { id: 'location', title: 'Location & Legal', helper: ' ' },
  { id: 'review', title: 'Review', helper: 'Check everything before saving' },
];

const EMPTY_PROJECT_DETAIL = {
  projectName: '',
  location: '',
  totalLandArea: '',
  totalUnits: '',
  configuration: '',
  price: '',
  status: 'draft',
  reraNumber: '',
};

const EMPTY_AMENITY = {
  title: '',
  desc: '',
  icon: '',
};

const AMENITY_ICON_OPTIONS = [
  { key: 'solar', label: 'Solar', Icon: FaSun },
  { key: 'fitness', label: 'Fitness', Icon: FaDumbbell },
  { key: 'lounge', label: 'Community', Icon: FaUsers },
  { key: 'jogging', label: 'Walking', Icon: FaPersonWalking },
  { key: 'children', label: 'Children', Icon: FaChild },
  { key: 'recreation', label: 'Park', Icon: FaTree },
  { key: 'landscape', label: 'Landscape', Icon: FaLeaf },
  { key: 'gathering', label: 'Gathering', Icon: FaUsers },
  { key: 'pathway', label: 'Pathway', Icon: FaShoePrints },
  { key: 'parking', label: 'Parking', Icon: FaCar },
  { key: 'security', label: 'Security', Icon: FaShieldHalved },
  { key: 'cctv', label: 'CCTV', Icon: FaVideo },
  { key: 'compound', label: 'Compound', Icon: FaCube },
  { key: 'roads', label: 'Roads', Icon: FaRoad },
  { key: 'lighting', label: 'Lighting', Icon: FaLightbulb },
  { key: 'ev', label: 'EV', Icon: FaChargingStation },
  { key: 'library', label: 'Library', Icon: FaBook },
];

function getAmenityIconOption(iconKey) {
  return AMENITY_ICON_OPTIONS.find((option) => option.key === iconKey) || null;
}

function filterAmenityIconOptions(searchValue) {
  const normalizedSearch = String(searchValue || '').trim().toLowerCase();

  if (!normalizedSearch) {
    return AMENITY_ICON_OPTIONS;
  }

  return AMENITY_ICON_OPTIONS.filter((option) => {
    return [option.key, option.label]
      .join(' ')
      .toLowerCase()
      .includes(normalizedSearch);
  });
}

function formatDateTime(value) {
  if (!value) {
    return '-';
  }

  const parsedDate = new Date(value);

  if (Number.isNaN(parsedDate.getTime())) {
    return '-';
  }

  return parsedDate.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function createPreviewEntry(file) {
  return {
    file,
    previewUrl: URL.createObjectURL(file),
    name: file.name || 'file',
    size: file.size || 0,
  };
}

function loadImageElement(imageSrc) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', () => reject(new Error('Could not process selected image.')));
    image.src = imageSrc;
  });
}

async function compressImageFile(file, options = {}) {
  const {
    maxDimension = 1920,
    quality = 0.82,
    outputType = 'image/jpeg',
    maxBytes = 1024 * 1024,
  } = options;

  if (file.size <= maxBytes && String(file.type || '').toLowerCase() === String(outputType || '').toLowerCase()) {
    return file;
  }

  const objectUrl = URL.createObjectURL(file);

  try {
    const image = await loadImageElement(objectUrl);
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    if (!context) {
      throw new Error('Could not process selected image.');
    }

    const createBlob = (mimeType, encoderQuality) => new Promise((resolve, reject) => {
      canvas.toBlob((result) => {
        if (!result) {
          reject(new Error('Could not compress selected image.'));
          return;
        }

        resolve(result);
      }, mimeType, encoderQuality);
    });

    let currentDimension = maxDimension;
    let currentQuality = Math.min(0.92, Math.max(0.45, quality));
    let lastBlob = null;

    for (let attempt = 0; attempt < 12; attempt += 1) {
      const scale = Math.min(1, currentDimension / image.width, currentDimension / image.height);
      const targetWidth = Math.max(1, Math.round(image.width * scale));
      const targetHeight = Math.max(1, Math.round(image.height * scale));

      canvas.width = targetWidth;
      canvas.height = targetHeight;
      context.clearRect(0, 0, targetWidth, targetHeight);
      context.drawImage(image, 0, 0, targetWidth, targetHeight);

      const blob = await createBlob(outputType, outputType === 'image/png' ? undefined : currentQuality);
      lastBlob = blob;

      if (blob.size <= maxBytes) {
        const baseName = String(file.name || 'image').replace(/\.[^/.]+$/, '') || 'image';
        const extension = outputType === 'image/png' ? 'png' : 'jpg';
        return new File([blob], `${baseName}-compressed.${extension}`, { type: outputType });
      }

      if (outputType !== 'image/png' && currentQuality > 0.52) {
        currentQuality = Math.max(0.45, currentQuality - 0.08);
      } else {
        currentDimension = Math.max(240, Math.round(currentDimension * 0.84));
      }
    }

    if (lastBlob && lastBlob.size <= maxBytes) {
      const baseName = String(file.name || 'image').replace(/\.[^/.]+$/, '') || 'image';
      const extension = outputType === 'image/png' ? 'png' : 'jpg';
      return new File([lastBlob], `${baseName}-compressed.${extension}`, { type: outputType });
    }

    throw new Error('Could not compress selected image below 1 MB. Please choose a smaller image.');
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function revokePreviewEntry(entry) {
  if (entry?.previewUrl && String(entry.previewUrl).startsWith('blob:')) {
    URL.revokeObjectURL(entry.previewUrl);
  }
}

function revokePreviewEntryList(entries) {
  for (const entry of entries || []) {
    revokePreviewEntry(entry);
  }
}

function formatFileSize(bytes) {
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return '0 KB';
  }

  if (bytes < 1024 * 1024) {
    return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  }

  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function normalizeArrayValue(value) {
  if (Array.isArray(value)) {
    return value.filter(Boolean).map((item) => String(item || '').trim()).filter(Boolean);
  }

  return [];
}

function countWords(value) {
  return String(value || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;
}

function normalizeSlugValue(value) {
  return String(value || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
}

function extractIframeSrc(value) {
  const rawValue = String(value || '').trim();

  if (!rawValue) {
    return '';
  }

  const iframeSrcMatch = rawValue.match(/<iframe[^>]*\ssrc=["']([^"']+)["'][^>]*>/i);
  return iframeSrcMatch?.[1] ? String(iframeSrcMatch[1]).trim() : '';
}

function createSafeVillaSlug(slugValue, fallbackValue) {
  const normalizedSlug = normalizeSlugValue(slugValue);
  const normalizedFallback = normalizeSlugValue(fallbackValue);
  const baseSlug = normalizedSlug || normalizedFallback || `villa-${Date.now()}`;

  return /^\d+$/.test(baseSlug) ? `villa-${baseSlug}` : baseSlug;
}

function isVillaSlugTaken(slugValue, villas, currentVillaId = null) {
  const normalizedSlug = normalizeSlugValue(slugValue);

  if (!normalizedSlug) {
    return false;
  }

  return (villas || []).some((villa) => {
    if (currentVillaId !== null && String(villa.id) === String(currentVillaId)) {
      return false;
    }

    return normalizeSlugValue(villa.slug) === normalizedSlug;
  });
}

function createEmptyVillaForm() {
  return {
    id: null,
    slug: '',
    name: '',
    location: '',
    acres: '',
    totalVillas: '',
    status: 'draft',
    description: '',
    overviewTitle: '',
    overviewDescription: '',
    overviewTotalLand: '',
    overviewTotalUnits: '',
    overviewEachPlot: '',
    overviewBuiltupAreaSqFt: '',
    configuration: '',
    startingPrice: '',
    reraNumber: '',
    mapLocationUrl: '',
    otherCharges: '',
    locationScanImageFile: null,
    locationScanImageName: '',
    reraScanImageFile: null,
    reraScanImageName: '',
    projectDetails: { ...EMPTY_PROJECT_DETAIL },
    projectHighlights: [''],
    locationAdvantages: [''],
    amenities: [{ ...EMPTY_AMENITY }],
    bannerImageFile: null,
    bannerImagePreviewUrl: '',
    existingBannerImageUrl: '',
    projectLogoFile: null,
    projectLogoPreviewUrl: '',
    existingProjectLogoUrl: '',
    projectLogoName: '',
    brochurePdfFile: null,
    brochurePdfName: '',
    existingBrochurePdfUrl: '',
    walkthroughVideoFile: null,
    walkthroughVideoName: '',
    existingWalkthroughVideoUrl: '',
    availabilityChartPdfFile: null,
    availabilityChartPdfName: '',
    existingAvailabilityChartPdfUrl: '',
    exteriorImages: [],
    existingExteriorImages: [],
    interiorImages: [],
    existingInteriorImages: [],
  };
}

function mapVillaToForm(villa) {
  const projectDetails = villa.projectDetails && typeof villa.projectDetails === 'object'
    ? villa.projectDetails
    : {};

  const bannerImageUrl = villa.bannerImage || villa.image || '';
  const projectLogoUrl = villa.projectLogo || villa.logo || '';
  const exteriorImages = Array.isArray(villa.images?.exterior)
    ? villa.images.exterior.filter((value) => value && value !== bannerImageUrl)
    : [];
  const interiorImages = Array.isArray(villa.images?.interior) ? villa.images.interior : [];

  return {
    ...createEmptyVillaForm(),
    id: villa.id,
    slug: normalizeSlugValue(villa.slug),
    name: villa.name || '',
    location: villa.location || '',
    acres: villa.acres || '',
    totalVillas: villa.totalVillas || '',
    status: villa.status || 'draft',
    description: villa.description || '',
    overviewTitle: villa.overviewTitle || '',
    overviewDescription: villa.overviewDescription || '',
    overviewTotalLand: villa.overviewTotalLand || '',
    overviewTotalUnits: villa.overviewTotalUnits || '',
    overviewEachPlot: villa.overviewEachPlot || projectDetails.overviewEachPlot || '',
    overviewBuiltupAreaSqFt: villa.overviewBuiltupAreaSqFt || projectDetails.overviewBuiltupAreaSqFt || '',
    configuration: villa.configuration || '',
    startingPrice: villa.startingPrice || villa.price || '',
    reraNumber: villa.reraNumber || projectDetails.reraNumber || '',
    mapLocationUrl: villa.mapLocationUrl || '',
    otherCharges: villa.otherCharges || '',
    projectDetails: {
      projectName: projectDetails.projectName || villa.name || '',
      location: projectDetails.location || villa.location || '',
      totalLandArea: projectDetails.totalLandArea || villa.acres || '',
      totalUnits: projectDetails.totalUnits || villa.totalVillas || '',
      overviewEachPlot: projectDetails.overviewEachPlot || villa.overviewEachPlot || '',
      overviewBuiltupAreaSqFt: projectDetails.overviewBuiltupAreaSqFt || villa.overviewBuiltupAreaSqFt || '',
      configuration: projectDetails.configuration || villa.configuration || '',
      price: projectDetails.price || villa.startingPrice || villa.price || '',
      status: projectDetails.status || villa.status || 'draft',
      reraNumber: projectDetails.reraNumber || villa.reraNumber || '',
      locationScanImageUrl: projectDetails.locationScanImageUrl || '',
      reraScanImageUrl: projectDetails.reraScanImageUrl || '',
    },
    projectHighlights: normalizeArrayValue(villa.highlights).concat(['']).slice(0, Math.max(1, normalizeArrayValue(villa.highlights).length + 1)),
    locationAdvantages: normalizeArrayValue(villa.locationAdvantages).concat(['']).slice(0, Math.max(1, normalizeArrayValue(villa.locationAdvantages).length + 1)),
    amenities: Array.isArray(villa.amenities) && villa.amenities.length > 0
      ? villa.amenities.map((item) => ({
        title: String(item.title || '').trim(),
        desc: String(item.desc || '').trim(),
        icon: String(item.icon || '').trim(),
      }))
      : [{ ...EMPTY_AMENITY }],
    existingBannerImageUrl: bannerImageUrl,
    existingBrochurePdfUrl: villa.brochurePdfUrl || '',
    existingWalkthroughVideoUrl: villa.walkthroughVideoUrl || '',
    existingAvailabilityChartPdfUrl: villa.availabilityChartPdfUrl || '',
    existingExteriorImages: exteriorImages,
    existingInteriorImages: interiorImages,
    existingProjectLogoUrl: projectLogoUrl,
  };
}

function cleanArray(values) {
  return values.map((item) => String(item || '').trim()).filter(Boolean);
}

function cleanAmenities(values) {
  return values
    .map((item) => ({
      title: String(item.title || '').trim(),
      desc: String(item.desc || '').trim(),
      icon: String(item.icon || '').trim(),
    }))
    .filter((item) => item.title || item.desc || item.icon);
}

function isBasicVillaFormComplete(form) {
  return Boolean(
    String(form.name || '').trim()
    && String(form.location || '').trim()
    && String(form.acres || '').trim()
    && String(form.totalVillas || '').trim()
    && (form.bannerImageFile || form.existingBannerImageUrl)
  );
}

function getNextStepIndex(currentStepIndex) {
  return Math.min(WIZARD_STEPS.length - 1, currentStepIndex + 1);
}

function isBasicOnlyVilla(form) {
  return String(form.status || '').trim().toLowerCase() === 'upcoming';
}

function isEnterKeyOnFormField(event) {
  const target = event.target;

  if (!target || target.isContentEditable) {
    return false;
  }

  const tagName = String(target.tagName || '').toLowerCase();
  if (tagName === 'textarea') {
    return false;
  }

  return tagName === 'input' || tagName === 'select';
}

function VillaProjectsAdmin({ token }) {
  const [villas, setVillas] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showForm, setShowForm] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [deletingVillaId, setDeletingVillaId] = useState(null);
  const [form, setForm] = useState(createEmptyVillaForm());
  const [amenityIconSearches, setAmenityIconSearches] = useState({});
  const [editingAmenityIndex, setEditingAmenityIndex] = useState(null);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [passwordConfirmInput, setPasswordConfirmInput] = useState('');
  const [passwordConfirmError, setPasswordConfirmError] = useState('');
  const [isVerifyingPassword, setIsVerifyingPassword] = useState(false);
  const [pendingDeleteVillaId, setPendingDeleteVillaId] = useState(null);
  const bannerInputRef = useRef(null);
  const projectLogoInputRef = useRef(null);
  const brochureInputRef = useRef(null);
  const walkthroughVideoInputRef = useRef(null);
  const availabilityInputRef = useRef(null);
  const locationScanInputRef = useRef(null);
  const reraScanInputRef = useRef(null);
  const exteriorInputRef = useRef(null);
  const interiorInputRef = useRef(null);
  const bannerPreviewRef = useRef('');
  const projectLogoPreviewRef = useRef('');
  const exteriorPreviewRef = useRef([]);
  const interiorPreviewRef = useRef([]);

  useEffect(() => {
    let mounted = true;

    async function loadVillas() {
      if (!token) {
        return;
      }

      setIsLoading(true);

      try {
        const data = await getAdminVillas(token);

        if (!mounted) {
          return;
        }

        setVillas(data.villas || []);
      } catch (error) {
        if (!mounted) {
          return;
        }

        setMessage({ type: 'error', text: error.message || 'Could not load villas.' });
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    }

    loadVillas();

    return () => {
      mounted = false;
    };
  }, [token]);

  useEffect(() => {
    bannerPreviewRef.current = form.bannerImagePreviewUrl;
  }, [form.bannerImagePreviewUrl]);

  useEffect(() => () => {
    revokePreviewEntry({ previewUrl: bannerPreviewRef.current });
  }, []);

  useEffect(() => {
    projectLogoPreviewRef.current = form.projectLogoPreviewUrl;
  }, [form.projectLogoPreviewUrl]);

  useEffect(() => () => {
    revokePreviewEntry({ previewUrl: projectLogoPreviewRef.current });
  }, []);

  useEffect(() => {
    exteriorPreviewRef.current = form.exteriorImages;
  }, [form.exteriorImages]);

  useEffect(() => () => {
    revokePreviewEntryList(exteriorPreviewRef.current);
  }, []);

  useEffect(() => {
    interiorPreviewRef.current = form.interiorImages;
  }, [form.interiorImages]);

  useEffect(() => () => {
    revokePreviewEntryList(interiorPreviewRef.current);
  }, []);

  const resetUploadInputs = () => {
    if (bannerInputRef.current) {
      bannerInputRef.current.value = '';
    }

    if (projectLogoInputRef.current) {
      projectLogoInputRef.current.value = '';
    }

    if (brochureInputRef.current) {
      brochureInputRef.current.value = '';
    }

    if (walkthroughVideoInputRef.current) {
      walkthroughVideoInputRef.current.value = '';
    }

    if (availabilityInputRef.current) {
      availabilityInputRef.current.value = '';
    }

    if (locationScanInputRef.current) {
      locationScanInputRef.current.value = '';
    }

    if (reraScanInputRef.current) {
      reraScanInputRef.current.value = '';
    }

    if (exteriorInputRef.current) {
      exteriorInputRef.current.value = '';
    }

    if (interiorInputRef.current) {
      interiorInputRef.current.value = '';
    }
  };

  const resetForm = () => {
    setForm((previous) => {
      revokePreviewEntry({ previewUrl: previous.bannerImagePreviewUrl });
      revokePreviewEntry({ previewUrl: previous.projectLogoPreviewUrl });
      revokePreviewEntryList(previous.exteriorImages);
      revokePreviewEntryList(previous.interiorImages);
      return createEmptyVillaForm();
    });
    setAmenityIconSearches({});
    resetUploadInputs();
    setActiveStep(0);
  };

  const openNewForm = () => {
    resetForm();
    setMessage({ type: '', text: '' });
    setEditingAmenityIndex(0);
    setShowForm(true);
  };

  const closeForm = () => {
    resetForm();
    setMessage({ type: '', text: '' });
    setShowForm(false);
  };

  const editVilla = (villa) => {
    setForm((previous) => {
      revokePreviewEntry({ previewUrl: previous.bannerImagePreviewUrl });
      revokePreviewEntry({ previewUrl: previous.projectLogoPreviewUrl });
      revokePreviewEntryList(previous.exteriorImages);
      revokePreviewEntryList(previous.interiorImages);
      return mapVillaToForm(villa);
    });
    setAmenityIconSearches({});
    setEditingAmenityIndex(null);
    resetUploadInputs();
    setMessage({ type: '', text: '' });
    setActiveStep(0);
    setShowForm(true);
  };

  const updateArrayField = (field, index, value) => {
    setForm((previous) => {
      const nextValues = [...previous[field]];
      nextValues[index] = value;
      return { ...previous, [field]: nextValues };
    });
  };

  const addArrayRow = (field, emptyValue) => {
    setForm((previous) => ({
      ...previous,
      [field]: [...previous[field], emptyValue],
    }));
  };

  const addAmenityRow = () => {
    setForm((previous) => ({
      ...previous,
      amenities: [...previous.amenities, { ...EMPTY_AMENITY }],
    }));
    setAmenityIconSearches({});
    setEditingAmenityIndex(form.amenities.length);
  };

  const updateAmenityField = (index, field, value) => {
    setForm((previous) => {
      const nextAmenities = [...previous.amenities];
      nextAmenities[index] = { ...nextAmenities[index], [field]: value };
      return { ...previous, amenities: nextAmenities };
    });
  };

  const updateAmenityIcon = (index, iconKey) => {
    setForm((previous) => {
      const nextAmenities = [...previous.amenities];
      nextAmenities[index] = { ...nextAmenities[index], icon: iconKey };
      return { ...previous, amenities: nextAmenities };
    });
  };

  const openAmenityEditor = (index) => {
    setEditingAmenityIndex(index);
    setAmenityIconSearches((previous) => ({
      ...previous,
      [index]: previous[index] || '',
    }));
  };

  const closeAmenityEditor = () => {
    setEditingAmenityIndex(null);
  };

  const removeAmenityRow = (index) => {
    setForm((previous) => {
      const nextAmenities = [...previous.amenities];
      nextAmenities.splice(index, 1);
      return {
        ...previous,
        amenities: nextAmenities.length > 0 ? nextAmenities : [{ ...EMPTY_AMENITY }],
      };
    });
    setAmenityIconSearches({});
    setEditingAmenityIndex((previous) => {
      if (previous === index) {
        return null;
      }

      if (previous !== null && previous > index) {
        return previous - 1;
      }

      return previous;
    });
  };

  const removeArrayRow = (field, index) => {
    setForm((previous) => {
      const nextValues = [...previous[field]];
      const removed = nextValues.splice(index, 1)[0];
      revokePreviewEntry(removed);
      return {
        ...previous,
        [field]: nextValues.length > 0 ? nextValues : [field === 'amenities' ? { ...EMPTY_AMENITY } : ''],
      };
    });
  };

  const removeGalleryImage = (field, index) => {
    setForm((previous) => {
      const nextValues = [...previous[field]];
      const removed = nextValues.splice(index, 1)[0];
      revokePreviewEntry(removed);

      return {
        ...previous,
        [field]: nextValues,
      };
    });
  };

  const renderGalleryImageGrid = (title, items, field, emptyLabel) => {
    if (!items.length) {
      return null;
    }

    return (
      <div className="space-y-2">
        <p className="text-sm text-primary">{title}</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {items.map((item, index) => {
            const isExistingImage = typeof item === 'string';
            const imageSrc = isExistingImage ? item : item.previewUrl;
            const imageName = isExistingImage ? emptyLabel || `Image ${index + 1}` : item.name;
            const imageSize = isExistingImage ? '' : formatFileSize(item.size);

            return (
              <div key={`${field}-${imageName}-${index}`} className="relative overflow-hidden rounded-xl border border-gray-200 bg-white">
                <img src={imageSrc} alt={imageName} className="h-28 w-full object-cover" />
                <button
                  type="button"
                  onClick={() => removeGalleryImage(field, index)}
                  className="absolute right-2 top-2 inline-flex h-8 w-8 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur-sm transition-colors hover:bg-black/75"
                  aria-label={`Remove ${imageName}`}
                >
                  <FaXmark className="text-sm" />
                </button>
                {!isExistingImage ? (
                  <div className="p-2">
                    <p className="text-[11px] text-primary truncate">{imageName}</p>
                    <p className="text-[10px] text-textGrey">{imageSize}</p>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const handleBannerSelection = async (event) => {
    const selectedFile = event.target.files && event.target.files[0] ? event.target.files[0] : null;

    if (!selectedFile) {
      return;
    }

    if (!String(selectedFile.type || '').startsWith('image/')) {
      setMessage({ type: 'error', text: 'Only image files are allowed.' });
      if (bannerInputRef.current) {
        bannerInputRef.current.value = '';
      }
      return;
    }

    try {
      const compressedFile = await compressImageFile(selectedFile);
      const nextPreviewUrl = URL.createObjectURL(compressedFile);

      setForm((previous) => {
        revokePreviewEntry({ previewUrl: previous.bannerImagePreviewUrl });
        return {
          ...previous,
          bannerImageFile: compressedFile,
          bannerImagePreviewUrl: nextPreviewUrl,
        };
      });

      setMessage({ type: '', text: '' });
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Could not compress selected image.' });
      if (bannerInputRef.current) {
        bannerInputRef.current.value = '';
      }
    }
  };

  const handleProjectLogoSelection = async (event) => {
    const selectedFile = event.target.files && event.target.files[0] ? event.target.files[0] : null;

    if (!selectedFile) {
      return;
    }

    const normalizedType = String(selectedFile.type || '').toLowerCase();
    const isPngType = normalizedType === 'image/png' || normalizedType === 'image/x-png';
    const hasPngExtension = /\.png$/i.test(String(selectedFile.name || '').trim());

    if (!isPngType && !hasPngExtension) {
      setMessage({ type: 'error', text: 'Logo must be a PNG image.' });
      if (projectLogoInputRef.current) {
        projectLogoInputRef.current.value = '';
      }
      return;
    }

    try {
      const compressedFile = await compressImageFile(selectedFile, { outputType: 'image/png' });
      const nextPreviewUrl = URL.createObjectURL(compressedFile);

      setForm((previous) => {
        revokePreviewEntry({ previewUrl: previous.projectLogoPreviewUrl });
        return {
          ...previous,
          projectLogoFile: compressedFile,
          projectLogoPreviewUrl: nextPreviewUrl,
          projectLogoName: compressedFile.name || selectedFile.name || 'selected file',
        };
      });

      setMessage({ type: '', text: '' });
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Could not compress selected image.' });
      if (projectLogoInputRef.current) {
        projectLogoInputRef.current.value = '';
      }
    }
  };

  const handleSingleFileSelection = async (field, nameField, event) => {
    const selectedFile = event.target.files && event.target.files[0] ? event.target.files[0] : null;

    if (!selectedFile) {
      return;
    }

    if ((field === 'locationScanImageFile' || field === 'reraScanImageFile') && !String(selectedFile.type || '').startsWith('image/')) {
      setMessage({ type: 'error', text: 'Only image files are allowed.' });
      event.target.value = '';
      return;
    }

    const shouldCompressImage = field === 'locationScanImageFile' || field === 'reraScanImageFile';

    if (!shouldCompressImage) {
      setForm((previous) => ({
        ...previous,
        [field]: selectedFile,
        [nameField]: selectedFile.name || 'selected file',
      }));
      return;
    }

    try {
      const compressedFile = await compressImageFile(selectedFile);

      setForm((previous) => ({
        ...previous,
        [field]: compressedFile,
        [nameField]: compressedFile.name || selectedFile.name || 'selected file',
      }));

      setMessage({ type: '', text: '' });
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Could not compress selected image.' });
      event.target.value = '';
    }
  };

  const handleMultiImageSelection = async (field, event) => {
    const incomingFiles = Array.from(event.target.files || []);

    if (!incomingFiles.length) {
      return;
    }

    const selectedFiles = incomingFiles.slice(0, 20);

    if (selectedFiles.some((file) => !String(file.type || '').startsWith('image/'))) {
      setMessage({ type: 'error', text: 'Only image files are allowed.' });
      if (field === 'exteriorImages' && exteriorInputRef.current) {
        exteriorInputRef.current.value = '';
      }
      if (field === 'interiorImages' && interiorInputRef.current) {
        interiorInputRef.current.value = '';
      }
      return;
    }

    try {
      const compressedFiles = await Promise.all(selectedFiles.map((file) => compressImageFile(file)));
      const previews = compressedFiles.map(createPreviewEntry);

      setForm((previous) => {
        revokePreviewEntryList(previous[field]);
        return {
          ...previous,
          [field]: previews,
        };
      });

      setMessage({ type: '', text: '' });
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Could not compress selected images.' });
    }
  };

  const clearMultiImages = (field) => {
    setForm((previous) => {
      revokePreviewEntryList(previous[field]);
      return {
        ...previous,
        [field]: [],
      };
    });

    if (field === 'exteriorImages' && exteriorInputRef.current) {
      exteriorInputRef.current.value = '';
    }

    if (field === 'interiorImages' && interiorInputRef.current) {
      interiorInputRef.current.value = '';
    }
  };

  const handleFormKeyDown = (event) => {
    if (event.key !== 'Enter' || event.shiftKey || event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }

    if (!isEnterKeyOnFormField(event)) {
      return;
    }

    if (!isBasicOnly && activeStep < WIZARD_STEPS.length - 1) {
      event.preventDefault();
      setActiveStep((previous) => Math.min(WIZARD_STEPS.length - 1, previous + 1));
    }
  };

  const submitVilla = async (event) => {
    event.preventDefault();
    setMessage({ type: '', text: '' });

    if (!form.name.trim() || !form.location.trim()) {
      setMessage({ type: 'error', text: 'Project name and location are required.' });
      return;
    }

    setIsSaving(true);

    try {
      const isFinalReviewStep = !isBasicOnly && activeStep === visibleSteps.length - 1;
      const nextStepIndex = getNextStepIndex(activeStep);
      const safeSlug = createSafeVillaSlug(form.slug, form.name);
      const payload = new FormData();
      payload.append('slug', safeSlug);
      payload.append('name', form.name.trim());
      payload.append('location', form.location.trim());
      payload.append('acres', form.acres.trim());
      payload.append('totalVillas', form.totalVillas.trim());
      payload.append('status', form.status);
      payload.append('description', form.description.trim());
      payload.append('overviewTitle', form.overviewTitle.trim());
      payload.append('overviewDescription', form.overviewDescription.trim());
      payload.append('overviewTotalLand', form.overviewTotalLand.trim());
      payload.append('overviewTotalUnits', form.overviewTotalUnits.trim());
      payload.append('configuration', form.configuration.trim());
      payload.append('startingPrice', form.startingPrice.trim());
      payload.append('reraNumber', form.reraNumber.trim());
      const mapEmbedSrc = extractIframeSrc(form.mapLocationUrl);
      payload.append('mapLocationUrl', mapEmbedSrc);
      payload.append('otherCharges', form.otherCharges.trim());
      payload.append('projectDetails', JSON.stringify({
        projectName: form.name.trim(),
        location: form.location.trim(),
        totalLandArea: form.acres.trim(),
        totalUnits: form.totalVillas.trim(),
        overviewEachPlot: form.overviewEachPlot.trim(),
        overviewBuiltupAreaSqFt: form.overviewBuiltupAreaSqFt.trim(),
        configuration: form.configuration.trim(),
        price: form.startingPrice.trim(),
        status: form.status,
        reraNumber: form.reraNumber.trim(),
      }));
      payload.append('projectHighlights', JSON.stringify(cleanArray(form.projectHighlights)));
      payload.append('locationAdvantages', JSON.stringify(cleanArray(form.locationAdvantages)));
      payload.append('amenities', JSON.stringify(cleanAmenities(form.amenities)));
      payload.append('existingExteriorImages', JSON.stringify(form.existingExteriorImages));
      payload.append('existingInteriorImages', JSON.stringify(form.existingInteriorImages));

      if (form.bannerImageFile) {
        payload.append('bannerImage', form.bannerImageFile);
      }

      if (form.projectLogoFile) {
        payload.append('projectLogo', form.projectLogoFile);
      }

      if (form.brochurePdfFile) {
        payload.append('brochurePdf', form.brochurePdfFile);
      }

      if (form.walkthroughVideoFile) {
        payload.append('walkthroughVideo', form.walkthroughVideoFile);
      }

      if (form.availabilityChartPdfFile) {
        payload.append('availabilityChartPdf', form.availabilityChartPdfFile);
      }

      if (form.locationScanImageFile) {
        payload.append('locationScanImage', form.locationScanImageFile);
      }

      if (form.reraScanImageFile) {
        payload.append('reraScanImage', form.reraScanImageFile);
      }

      form.exteriorImages.forEach((item) => {
        if (item.file) {
          payload.append('exteriorImages', item.file);
        }
      });

      form.interiorImages.forEach((item) => {
        if (item.file) {
          payload.append('interiorImages', item.file);
        }
      });

      const saveResponse = form.id
        ? await updateAdminVilla(token, form.id, payload)
        : await createAdminVilla(token, payload);

      const savedVilla = saveResponse?.villa;

      if (savedVilla) {
        setForm((previous) => {
          revokePreviewEntry({ previewUrl: previous.bannerImagePreviewUrl });
          revokePreviewEntry({ previewUrl: previous.projectLogoPreviewUrl });
          revokePreviewEntryList(previous.exteriorImages);
          revokePreviewEntryList(previous.interiorImages);
          return mapVillaToForm(savedVilla);
        });
      }

      setMessage({
        type: 'success',
        text: form.id ? 'Villa updated successfully.' : 'Villa created successfully.',
      });

      if (isFinalReviewStep) {
        window.alert(form.id ? 'Villa updated successfully.' : 'Villa created successfully.');
        closeForm();
      } else if (!isBasicOnly && nextStepIndex > activeStep) {
        setActiveStep(nextStepIndex);
      }

      // Clear the save state as soon as persistence succeeds.
      setIsSaving(false);

      // Refresh list in the background so a slow fetch does not keep the form stuck in "Saving...".
      getAdminVillas(token)
        .then((data) => {
          setVillas(data.villas || []);
        })
        .catch(() => {
          // Keep success state because the save already completed.
        });
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Could not save villa.' });
    } finally {
      setIsSaving(false);
    }
  };

  const deleteVilla = (villaId) => {
    setPendingDeleteVillaId(villaId);
    setPasswordConfirmInput('');
    setPasswordConfirmError('');
    setShowPasswordConfirm(true);
  };

  const cancelPasswordConfirm = () => {
    setShowPasswordConfirm(false);
    setPasswordConfirmInput('');
    setPasswordConfirmError('');
    setPendingDeleteVillaId(null);
  };

  const confirmDeleteWithPassword = async (event) => {
    event.preventDefault();
    setPasswordConfirmError('');

    if (!passwordConfirmInput.trim()) {
      setPasswordConfirmError('Password is required.');
      return;
    }

    setIsVerifyingPassword(true);

    try {
      await verifyAdminPassword(token, passwordConfirmInput);
      
      // Password verified, now delete the villa
      setShowPasswordConfirm(false);
      setPasswordConfirmInput('');
      setIsVerifyingPassword(false);
      
      setDeletingVillaId(pendingDeleteVillaId);
      setMessage({ type: '', text: '' });

      try {
        await deleteAdminVilla(token, pendingDeleteVillaId);

        if (form.id === pendingDeleteVillaId) {
          closeForm();
        }

        const data = await getAdminVillas(token);
        setVillas(data.villas || []);
        setMessage({ type: 'success', text: 'Villa deleted successfully.' });
      } catch (error) {
        setMessage({ type: 'error', text: error.message || 'Could not delete villa.' });
      } finally {
        setDeletingVillaId(null);
        setPendingDeleteVillaId(null);
      }
    } catch (error) {
      setPasswordConfirmError(error.message || 'Password verification failed.');
      setIsVerifyingPassword(false);
    }
  };

  const currentStep = WIZARD_STEPS[activeStep] || WIZARD_STEPS[0];
  const isEditMode = Boolean(form.id);
  const isBasicComplete = isEditMode || isBasicVillaFormComplete(form);
  const isBasicOnly = isBasicOnlyVilla(form);
  const visibleSteps = isBasicOnly ? WIZARD_STEPS.slice(0, 1) : WIZARD_STEPS;
  const saveButtonLabel = isBasicOnly || activeStep === visibleSteps.length - 1 ? 'Save' : 'Save and Next';
  const effectiveSlug = createSafeVillaSlug(form.slug, form.name);
  const isSlugDuplicate = isVillaSlugTaken(effectiveSlug, villas, form.id);
  const descriptionWordCount = countWords(form.description);

  useEffect(() => {
    if (isBasicOnly && activeStep !== 0) {
      setActiveStep(0);
    }
  }, [activeStep, isBasicOnly]);

  if (!token) {
    return null;
  }

  const renderStepContent = () => {
    switch (currentStep.id) {
      case 'basic':
        return (
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <input
                type="text"
                placeholder="Project name"
                value={form.name}
                onChange={(event) => setForm((previous) => ({ ...previous, name: event.target.value }))}
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-accent"
                required
              />
              <input
                type="text"
                placeholder="Project ID"
                value={form.slug}
                onChange={(event) => setForm((previous) => ({
                  ...previous,
                  slug: normalizeSlugValue(event.target.value),
                }))}
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-accent"
              />
              {isSlugDuplicate ? (
                <p className="sm:col-span-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  This project ID is already in use. A unique version will be saved.
                </p>
              ) : null}
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <input
                type="text"
                placeholder="Location"
                value={form.location}
                onChange={(event) => setForm((previous) => ({ ...previous, location: event.target.value }))}
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-accent"
                required
              />
              <input
                type="text"
                placeholder="Acres (land)"
                value={form.acres}
                onChange={(event) => setForm((previous) => ({ ...previous, acres: event.target.value }))}
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <input
                type="text"
                placeholder="Total villas"
                value={form.totalVillas}
                onChange={(event) => setForm((previous) => ({ ...previous, totalVillas: event.target.value }))}
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-accent"
              />
              <div className="rounded-xl border border-gray-200 bg-white p-4">
                <p className="text-primary mb-2">Status</p>
                <select
                  value={form.status}
                  onChange={(event) => setForm((previous) => ({ ...previous, status: event.target.value }))}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-accent"
                >
                  <option value="draft">Draft</option>
                  <option value="ongoing">Ongoing</option>
                  <option value="upcoming">Upcoming</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
              <div className="rounded-2xl border border-primary/15 bg-white p-4">
                <input
                  ref={projectLogoInputRef}
                  id="villa-logo-upload"
                  type="file"
                  accept="image/png,.png"
                  onChange={handleProjectLogoSelection}
                  className="hidden"
                />
                <div className="flex flex-wrap items-center gap-3">
                  <label
                    htmlFor="villa-logo-upload"
                    className="cursor-pointer inline-flex items-center justify-center rounded-lg border border-primary bg-[#EF1C22] text-white px-4 py-2 text-sm hover:bg-opacity-90"
                  >
                    {form.id ? 'Replace Project Logo' : 'Choose Project Logo'}
                  </label>
                  {form.projectLogoFile ? (
                    <span className="text-xs sm:text-sm text-textGrey truncate max-w-[280px]">{form.projectLogoName || form.projectLogoFile.name}</span>
                  ) : form.existingProjectLogoUrl ? (
                    <span className="text-xs sm:text-sm text-textGrey truncate max-w-[280px]">Current project logo is set</span>
                  ) : (
                    <span className="text-xs sm:text-sm text-textGrey">No logo selected</span>
                  )}
                </div>
              </div>
              {form.projectLogoPreviewUrl ? (
                <div className="rounded-2xl border border-primary/15 bg-white p-4 sm:col-span-2">
                  <img src={form.projectLogoPreviewUrl} alt="Project logo preview" className="h-24 w-full rounded-xl border border-gray-200 bg-white object-contain p-3" />
                </div>
              ) : form.existingProjectLogoUrl ? (
                <div className="rounded-2xl border border-primary/15 bg-white p-4 sm:col-span-2">
                  <img src={form.existingProjectLogoUrl} alt="Current project logo" className="h-24 w-full rounded-xl border border-gray-200 bg-white object-contain p-3" />
                </div>
              ) : null}
              <div className="rounded-2xl border border-primary/15 bg-white p-4 sm:col-span-2">
                <input
                  ref={bannerInputRef}
                  id="villa-banner-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleBannerSelection}
                  className="hidden"
                />
                <div className="flex flex-wrap items-center gap-3">
                  <label
                    htmlFor="villa-banner-upload"
                    className="cursor-pointer inline-flex items-center justify-center rounded-lg border border-primary bg-[#EF1C22] text-white px-4 py-2 text-sm hover:bg-opacity-90"
                  >
                    {form.id ? 'Replace Banner Image' : 'Choose Banner Image'}
                  </label>
                  {form.bannerImageFile ? (
                    <span className="text-xs sm:text-sm text-textGrey truncate max-w-[280px]">{form.bannerImageFile.name}</span>
                  ) : form.existingBannerImageUrl ? (
                    <span className="text-xs sm:text-sm text-textGrey truncate max-w-[280px]">Current banner image is set</span>
                  ) : (
                    <span className="text-xs sm:text-sm text-textGrey">No file selected</span>
                  )}
                </div>
              </div>
            </div>
            {form.bannerImagePreviewUrl ? (
              <img src={form.bannerImagePreviewUrl} alt="Banner preview" className="h-48 w-full rounded-xl object-cover border border-gray-200" />
            ) : form.existingBannerImageUrl ? (
              <img src={form.existingBannerImageUrl} alt="Current banner" className="h-48 w-full rounded-xl object-cover border border-gray-200" />
            ) : null}

            {isBasicOnly ? (
              <p className="text-sm text-textGrey">
                Upcoming villas only need the Basic Data section. You can save now and return later to add the rest.
              </p>
            ) : null}
          </div>
        );
      case 'detailed':
        return (
          <div className="space-y-4">
            <div className="grid gap-3 lg:grid-cols-[1.1fr_0.9fr] items-start">
              <div className="rounded-xl border border-gray-200 bg-white p-4">
                <p className="text-primary mb-2">Brochure PDF</p>
                <input
                  ref={brochureInputRef}
                  type="file"
                  accept="application/pdf"
                  onChange={(event) => handleSingleFileSelection('brochurePdfFile', 'brochurePdfName', event)}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-accent bg-white"
                />
                <p className="mt-2 text-sm text-textGrey">{form.brochurePdfName || (form.existingBrochurePdfUrl ? 'Current brochure is set' : 'No file selected')}</p>
              </div> 
                <div className="rounded-xl border border-gray-200 bg-white p-4">
                  <p className="text-primary mb-2">Walkthrough Video</p>
                  <input
                    ref={walkthroughVideoInputRef}
                    type="file"
                    accept="video/*"
                    onChange={(event) => handleSingleFileSelection('walkthroughVideoFile', 'walkthroughVideoName', event)}
                    className="w-full text-sm"
                  />
                  <p className="mt-2">{form.walkthroughVideoName || (form.existingWalkthroughVideoUrl ? 'Current walkthrough is set' : 'No file selected')}</p>
                </div> 
            </div>


            <textarea
              placeholder="Description"
              value={form.description}
              onChange={(event) => {
                const nextValue = event.target.value;

                setForm((previous) => ({
                  ...previous,
                  description: countWords(nextValue) > 50 ? previous.description : nextValue,
                }));
              }}
              rows={4}
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-accent resize-none"
            />
            <div className="flex flex-col items-end gap-1 text-xs">
              {descriptionWordCount >= 45 ? (
                <p className={`font-medium ${descriptionWordCount >= 50 ? 'text-red-600' : 'text-amber-600'}`}>
                  {descriptionWordCount >= 50 ? 'Word limit reached.' : 'You are close to the 50-word limit.'}
                </p>
              ) : null}
              <p className="text-textGrey">{descriptionWordCount} / 50 words</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <input
                type="text"
                placeholder="Overview title"
                value={form.overviewTitle}
                onChange={(event) => setForm((previous) => ({ ...previous, overviewTitle: event.target.value }))}
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-accent"
              />
              <input
                type="text"
                placeholder="Starting price / Sq.Ft"
                value={form.startingPrice}
                onChange={(event) => setForm((previous) => ({ ...previous, startingPrice: event.target.value }))}
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </div>
            <textarea
              placeholder="Overview description"
              value={form.overviewDescription}
              onChange={(event) => setForm((previous) => ({ ...previous, overviewDescription: event.target.value }))}
              rows={4}
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-accent resize-none"
            />
            <div className="grid sm:grid-cols-2 gap-3">
              <input
                type="text"
                placeholder="Overview total land"
                value={form.overviewTotalLand}
                onChange={(event) => setForm((previous) => ({ ...previous, overviewTotalLand: event.target.value }))}
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-accent"
              />
              <input
                type="text"
                placeholder="Overview total units"
                value={form.overviewTotalUnits}
                onChange={(event) => setForm((previous) => ({ ...previous, overviewTotalUnits: event.target.value }))}
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-accent"
              />
              <input
                type="text"
                placeholder="Each plot (Cent)"
                value={form.overviewEachPlot}
                onChange={(event) => setForm((previous) => ({ ...previous, overviewEachPlot: event.target.value }))}
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-accent"
              />
              <input
                type="text"
                placeholder="Built-up area Sq.Ft"
                value={form.overviewBuiltupAreaSqFt}
                onChange={(event) => setForm((previous) => ({ ...previous, overviewBuiltupAreaSqFt: event.target.value }))}
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <input
                type="text"
                placeholder="Configuration"
                value={form.configuration}
                onChange={(event) => setForm((previous) => ({ ...previous, configuration: event.target.value }))}
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-accent"
              />
              <input
                type="text"
                placeholder="RERA number"
                value={form.reraNumber}
                onChange={(event) => setForm((previous) => ({ ...previous, reraNumber: event.target.value }))}
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </div>
            <textarea
              placeholder="Other charges"
              value={form.otherCharges}
              onChange={(event) => setForm((previous) => ({ ...previous, otherCharges: event.target.value }))}
              rows={3}
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-accent resize-none"
            />
          </div>
        );
      case 'gallery':
        return (
          <div className="space-y-4">
            <div className="rounded-2xl border border-primary/15 bg-white p-4">
              <input
                ref={exteriorInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={(event) => handleMultiImageSelection('exteriorImages', event)}
                className="hidden"
                id="villa-exterior-upload"
              />
              <div className="flex flex-wrap items-center gap-3">
                <label htmlFor="villa-exterior-upload" className="cursor-pointer inline-flex items-center justify-center rounded-lg border border-primary bg-[#EF1C22] text-white px-4 py-2 text-sm hover:bg-opacity-90">Choose Exterior Images</label>
              </div>
              <div className="mt-4 space-y-4">
                {renderGalleryImageGrid('Selected exterior images', form.exteriorImages, 'exteriorImages', 'Exterior image')}
                {renderGalleryImageGrid('Current exterior images', form.existingExteriorImages, 'existingExteriorImages', 'Exterior image')}
              </div>
            </div>
            <div className="rounded-2xl border border-primary/15 bg-white p-4">
              <input
                ref={interiorInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={(event) => handleMultiImageSelection('interiorImages', event)}
                className="hidden"
                id="villa-interior-upload"
              />
              <div className="flex flex-wrap items-center gap-3">
                <label htmlFor="villa-interior-upload" className="cursor-pointer inline-flex items-center justify-center rounded-lg border border-primary bg-[#EF1C22] text-white px-4 py-2 text-sm hover:bg-opacity-90">Choose Interior Images</label>
              </div>
              <div className="mt-4 space-y-4">
                {renderGalleryImageGrid('Selected interior images', form.interiorImages, 'interiorImages', 'Interior image')}
                {renderGalleryImageGrid('Current interior images', form.existingInteriorImages, 'existingInteriorImages', 'Interior image')}
              </div>
            </div>
          </div>
        );
      case 'highlights':
        return (
          <div className="space-y-3">
            {form.projectHighlights.map((highlight, index) => (
              <div key={`highlight-${index}`} className="flex gap-3">
                <input
                  type="text"
                  placeholder={`Highlight ${index + 1}`}
                  value={highlight}
                  onChange={(event) => updateArrayField('projectHighlights', index, event.target.value)}
                  className="flex-1 px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-accent"
                />
                <button type="button" onClick={() => removeArrayRow('projectHighlights', index)} className="px-4 py-3 rounded-xl border border-gray-300 text-sm text-red-600">Remove</button>
              </div>
            ))}
            <button type="button" onClick={() => addArrayRow('projectHighlights', '')} className="text-sm text-accent">Add highlight</button>
          </div>
        );
      case 'details':
        return (
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="rounded-xl bg-white border border-gray-200 p-4">
                <p className="text-xs uppercase tracking-wide text-textGrey">Project name</p>
                <p className="mt-1 text-primary">{form.name || 'Not set'}</p>
              </div>
              <div className="rounded-xl bg-white border border-gray-200 p-4">
                <p className="text-xs uppercase tracking-wide text-textGrey">Location</p>
                <p className="mt-1 text-primary">{form.location || 'Not set'}</p>
              </div>
              <div className="rounded-xl bg-white border border-gray-200 p-4">
                <p className="text-xs uppercase tracking-wide text-textGrey">Total land</p>
                <p className="mt-1 text-primary">{form.acres || 'Not set'}</p>
              </div>
              <div className="rounded-xl bg-white border border-gray-200 p-4">
                <p className="text-xs uppercase tracking-wide text-textGrey">Total units</p>
                <p className="mt-1 text-primary">{form.totalVillas || 'Not set'}</p>
              </div>
              <div className="rounded-xl bg-white border border-gray-200 p-4">
                <p className="text-xs uppercase tracking-wide text-textGrey">Configuration</p>
                <p className="mt-1 text-primary">{form.configuration || 'Not set'}</p>
              </div>
              <div className="rounded-xl bg-white border border-gray-200 p-4">
                <p className="text-xs uppercase tracking-wide text-textGrey">Starting price</p>
                <p className="mt-1 text-primary">{form.startingPrice || 'Not set'}</p>
              </div>
            </div>
            <div className="rounded-xl bg-white border border-gray-200 p-4">
              <p className="text-xs uppercase tracking-wide text-textGrey">Description</p>
              <p className="mt-1 text-primary">{form.description || 'Not set'}</p>
            </div>
            <div className="rounded-xl bg-white border border-gray-200 p-4">
              <p className="text-xs uppercase tracking-wide text-textGrey">RERA number</p>
              <p className="mt-1 text-primary">{form.reraNumber || 'Not set'}</p>
            </div>
          </div>
        );
      case 'amenities':
        return (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-textGrey">Add amenities as cards, then open any card to edit its title, description, and icon.</p>
              <button type="button" onClick={addAmenityRow} className="rounded-full bg-[#EF1C22] px-4 py-2 text-sm text-white hover:bg-opacity-90">
                Add amenity
              </button>
            </div>

            <div className="grid gap-4">
              {form.amenities.map((item, index) => {
                const selectedIconOption = getAmenityIconOption(item.icon);
                const SelectedIcon = selectedIconOption?.Icon;
                const isEditing = editingAmenityIndex === index;

                return (
                  <div key={`amenity-${index}`} className="rounded-2xl border border-gray-200 bg-bgLight/40 p-4 space-y-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex items-start gap-3 min-w-0">
                        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white border border-gray-200 text-accent">
                          {SelectedIcon ? <SelectedIcon className="text-xl" /> : <FaCheck className="text-base text-textGrey" />}
                        </div>
                        <div className="min-w-0">
                          <h5 className="text-primary truncate">{item.title || 'Untitled amenity'}</h5>
                          <p className="text-sm text-textGrey line-clamp-2">{item.desc || 'No description set yet.'}</p>
                          <p className="mt-1 text-xs text-textGrey">{selectedIconOption?.label || 'No icon selected'}</p>
                        </div>
                      </div>

                      <div className="flex shrink-0 items-center gap-2">
                        <button type="button" onClick={() => (isEditing ? closeAmenityEditor() : openAmenityEditor(index))} className="rounded-full border border-gray-300 bg-white px-3 py-2 text-xs text-primary">
                          {isEditing ? 'Done' : 'Edit'}
                        </button>
                        <button type="button" onClick={() => removeAmenityRow(index)} className="rounded-full border border-gray-300 bg-white px-3 py-2 text-xs text-red-600">
                          Remove
                        </button>
                      </div>
                    </div>

                    {isEditing ? (
                      <div className="space-y-4 border-t border-gray-200 pt-4">
                        <div className="grid gap-3 md:grid-cols-[1fr_1fr_auto] items-start">
                          <input
                            type="text"
                            placeholder="Title"
                            value={item.title}
                            onChange={(event) => updateAmenityField(index, 'title', event.target.value)}
                            className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-accent"
                          />
                          <input
                            type="text"
                            placeholder="Description"
                            value={item.desc}
                            onChange={(event) => updateAmenityField(index, 'desc', event.target.value)}
                            className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-accent"
                          />
                          <button type="button" onClick={() => updateAmenityField(index, 'icon', '')} className="px-4 py-3 rounded-xl border border-gray-300 text-sm text-red-600">
                            Clear icon
                          </button>
                        </div>

                        <div className="space-y-3">
                          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                            <div className="relative flex-1">
                              <FaMagnifyingGlass className="absolute left-4 top-1/2 -translate-y-1/2 text-textGrey" />
                              <input
                                type="text"
                                value={amenityIconSearches[index] || ''}
                                onChange={(event) => setAmenityIconSearches((previous) => ({ ...previous, [index]: event.target.value }))}
                                placeholder="Search icons"
                                className="w-full pl-11 pr-10 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-accent"
                              />
                              {amenityIconSearches[index] ? (
                                <button
                                  type="button"
                                  onClick={() => setAmenityIconSearches((previous) => ({ ...previous, [index]: '' }))}
                                  className="absolute right-3 top-1/2 -translate-y-1/2 text-textGrey hover:text-primary"
                                  aria-label="Clear icon search"
                                >
                                  <FaXmark />
                                </button>
                              ) : null}
                            </div>
                            {selectedIconOption ? (
                              <div className="inline-flex items-center gap-2 rounded-xl bg-white border border-gray-200 px-4 py-3 text-sm text-primary">
                                {SelectedIcon ? <SelectedIcon className="text-accent" /> : null}
                                <span>{selectedIconOption.label}</span>
                              </div>
                            ) : (
                              <div className="text-sm text-textGrey px-1 py-3">No icon selected</div>
                            )}
                          </div>

                          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                            {filterAmenityIconOptions(amenityIconSearches[index] || '').map((option) => {
                              const AmenityIcon = option.Icon;
                              const isSelected = item.icon === option.key;

                              return (
                                <button
                                  key={option.key}
                                  type="button"
                                  onClick={() => updateAmenityIcon(index, option.key)}
                                  className={`flex items-center justify-between gap-3 rounded-xl border px-3 py-3 text-left transition-colors ${isSelected ? 'border-accent bg-accent/10' : 'border-gray-200 bg-white hover:border-accent/50'}`}
                                >
                                  <span className="flex items-center gap-2 min-w-0">
                                    <AmenityIcon className="shrink-0 text-accent" />
                                    <span className="truncate text-sm text-primary">{option.label}</span>
                                  </span>
                                  {isSelected ? <FaCheck className="shrink-0 text-accent" /> : null}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        );
      case 'availability':
        return (
          <div className="rounded-2xl border border-primary/15 bg-white p-4 space-y-3">
            <input
              ref={availabilityInputRef}
              type="file"
              accept="application/pdf"
              onChange={(event) => handleSingleFileSelection('availabilityChartPdfFile', 'availabilityChartPdfName', event)}
              className="w-full text-sm"
            />
            <p className="text-sm text-textGrey">{form.availabilityChartPdfName || (form.existingAvailabilityChartPdfUrl ? 'Current availability chart is set' : 'No file selected')}</p>
          </div>
        );
      case 'location':
        {
          const mapEmbedSrc = extractIframeSrc(form.mapLocationUrl);

        return (
          <div className="space-y-4">
            <div className="rounded-2xl border border-gray-200 bg-white p-4 space-y-3">
              <div className="flex items-center gap-2 text-sm text-primary">
                <FaMapLocationDot className="text-accent" />
                Paste Google Maps iframe code only
              </div>
              <textarea
                rows={3}
                placeholder="Paste full iframe embed code from Google Maps"
                value={form.mapLocationUrl}
                onChange={(event) => setForm((previous) => ({ ...previous, mapLocationUrl: event.target.value }))}
                className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-accent"
              />
              <p className="text-xs text-textGrey">
                Simple step: open Google Maps, click Share, choose Embed a map, copy the iframe HTML, and paste it here.
              </p>
              {form.mapLocationUrl && !mapEmbedSrc ? (
                <p className="text-xs text-red-600">
                  Invalid input. Paste full iframe code containing a src attribute.
                </p>
              ) : null}

            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              <div className="rounded-2xl border border-gray-200 bg-white p-4 space-y-3">
                <p className="text-primary">Location scan image</p>
                <input
                  ref={locationScanInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(event) => handleSingleFileSelection('locationScanImageFile', 'locationScanImageName', event)}
                  className="w-full text-sm"
                />
                <p className="text-sm text-textGrey">
                  {form.locationScanImageName || (form.projectDetails.locationScanImageUrl ? 'Current location scan image is set' : 'No file selected')}
                </p>
              </div>

              <div className="rounded-2xl border border-gray-200 bg-white p-4 space-y-3">
                <p className="text-primary">RERA scan image</p>
                <input
                  ref={reraScanInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(event) => handleSingleFileSelection('reraScanImageFile', 'reraScanImageName', event)}
                  className="w-full text-sm"
                />
                <p className="text-sm text-textGrey">
                  {form.reraScanImageName || (form.projectDetails.reraScanImageUrl ? 'Current RERA scan image is set' : 'No file selected')}
                </p>
              </div>
            </div>
             {form.locationAdvantages.map((item, index) => (
              <div key={`advantage-${index}`} className="flex gap-3">
                <input
                  type="text"
                  placeholder={`Location advantage ${index + 1}`}
                  value={item}
                  onChange={(event) => updateArrayField('locationAdvantages', index, event.target.value)}
                  className="flex-1 px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-accent"
                />
                <button type="button" onClick={() => removeArrayRow('locationAdvantages', index)} className="px-4 py-3 rounded-xl border border-gray-300 text-sm text-red-600">Remove</button>
              </div>
            ))}
            <button type="button" onClick={() => addArrayRow('locationAdvantages', '')} className="text-sm text-accent">Add location advantage</button> 
          </div>
        );
      }
      case 'review':
      default:
        return (
          <div className="space-y-4 text-sm text-textGrey">
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="rounded-xl bg-white border border-gray-200 p-4">
                <p className="text-xs uppercase tracking-wide text-textGrey">Project</p>
                <p className="mt-1 text-primary">{form.name || 'Unnamed villa'}</p>
                <p>{form.location || 'No location set'}</p>
              </div>
              <div className="rounded-xl bg-white border border-gray-200 p-4">
                <p className="text-xs uppercase tracking-wide text-textGrey">Status</p>
                <p className="mt-1 text-primary">{form.status}</p>
                <p>{createSafeVillaSlug(form.slug, form.name) || 'Slug will be generated from name'}</p>
              </div>
            </div>
            <div className="rounded-xl bg-white border border-gray-200 p-4">
              <p className="text-xs uppercase tracking-wide text-textGrey mb-2">Highlights</p>
              <p>{cleanArray(form.projectHighlights).length || 0} items</p>
            </div>
            <div className="rounded-xl bg-white border border-gray-200 p-4">
              <p className="text-xs uppercase tracking-wide text-textGrey mb-2">Amenities</p>
              <p>{cleanAmenities(form.amenities).length || 0} items</p>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="mt-6 space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-2xl text-primary">Villa Projects</h3>
        </div>
        {!showForm ? (
          <button
            type="button"
            onClick={openNewForm}
            className="inline-flex items-center justify-center rounded-full bg-[#EF1C22] text-white px-4 py-2 text-sm hover:bg-opacity-90"
          >
            Add New Villa
          </button>
        ) : null}
      </div>

      {showForm ? (
        <form onSubmit={submitVilla} onKeyDown={handleFormKeyDown} className="bg-bgLight rounded-2xl p-4 sm:p-6 space-y-5">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h3 className="text-2xl text-primary">
                {form.id
                  ? `Edit ${form.name.trim() ? `${form.name.trim()}` : 'Villa'}`
                  : `Create ${form.name.trim() ? `${form.name.trim()}` : 'Villa'}`}
              </h3>
            </div>
            <button type="button" onClick={closeForm} className="text-sm text-accent">Close</button>
          </div>

          <div className="flex flex-wrap gap-2">
            {visibleSteps.map((step, index) => (
              <button
                key={step.id}
                type="button"
                onClick={() => {
                  if (index > 0 && !isBasicComplete) {
                    return;
                  }

                  setActiveStep(index);
                }}
                disabled={index > 0 && !isBasicComplete}
                className={`rounded-full px-3 py-2 text-xs transition-colors ${activeStep === index
                    ? 'bg-[#EF1C22] text-white'
                    : index > 0 && !isBasicComplete
                      ? 'bg-white text-primary border border-gray-200 opacity-45 cursor-not-allowed'
                      : 'bg-white text-primary border border-gray-200'
                  }`}
              >
                {index + 1}. {step.title}
              </button>
            ))}
          </div>

          {!isBasicComplete && !isBasicOnly ? (
            <p className="text-sm text-textGrey">
              Complete the Basic Data section to unlock the remaining steps.
            </p>
          ) : null}

          {isBasicOnly ? (
            <p className="text-sm text-textGrey">
              This villa is marked as upcoming, so only Basic Data is required.
            </p>
          ) : null}

          <div className="rounded-2xl bg-white border border-gray-200 p-4 sm:p-6">
            <div className="mb-4">
              <p className="text-xs uppercase tracking-[0.2em] text-accent">Step {activeStep + 1}</p>
              <h4 className="text-2xl text-primary mt-1">{currentStep.title}</h4>
              <p className="text-sm text-textGrey mt-1">{currentStep.helper}</p>
            </div>

            {renderStepContent()}

            <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setActiveStep((previous) => Math.max(0, previous - 1))}
                  disabled={activeStep === 0}
                  className="px-4 py-2 rounded-luxury border border-gray-300 text-sm text-textGrey disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  type="button"
                  onClick={() => setActiveStep((previous) => Math.min(visibleSteps.length - 1, previous + 1))}
                  disabled={visibleSteps.length === 1 || activeStep === visibleSteps.length - 1 || (!isBasicComplete && activeStep === 0)}
                  className="px-4 py-2 rounded-luxury border border-gray-300 text-sm text-textGrey disabled:opacity-50"
                >
                  Next
                </button>
              </div>

              <button
                type="submit"
                disabled={isSaving}
                className="bg-primary text-white px-6 py-3 rounded-luxury hover:bg-opacity-90 transition-colors disabled:opacity-70"
              >
                {isSaving ? 'Saving...' : saveButtonLabel}
              </button>
            </div>
          </div>
        </form>
      ) : null}

      {message.text ? (
        <p className={`text-sm ${message.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>{message.text}</p>
      ) : null}

      <div className="bg-white border border-gray-200 rounded-2xl p-4 sm:p-6 overflow-x-auto">
        <table className="w-full min-w-[980px] border-collapse">
          <thead>
            <tr className="bg-bgLight text-left">
              <th className="px-4 py-3 text-sm text-primary">Villa</th>
              <th className="px-4 py-3 text-sm text-primary">Status</th>
              <th className="px-4 py-3 text-sm text-primary">Location</th>
              <th className="px-4 py-3 text-sm text-primary">Slug</th>
              <th className="px-4 py-3 text-sm text-primary">Updated</th>
              <th className="px-4 py-3 text-sm text-primary">Actions</th>
            </tr>
          </thead>
          <tbody>
            {villas.map((villa) => (
              <tr key={villa.id} className="border-b border-gray-100">
                <td className="px-4 py-3 text-sm text-primary">
                  <p>{villa.name}</p>
                  <p className="text-xs text-textGrey">{villa.acres || '-'} land</p>
                </td>
                <td className="px-4 py-3 text-sm text-textGrey capitalize">{villa.status || '-'}</td>
                <td className="px-4 py-3 text-sm text-textGrey">{villa.location || '-'}</td>
                <td className="px-4 py-3 text-sm text-textGrey truncate">{villa.slug || '-'}</td>
                <td className="px-4 py-3 text-sm text-textGrey">{formatDateTime(villa.updatedAt || villa.createdAt)}</td>
                <td className="px-4 py-3 text-sm">
                  <div className="flex items-center gap-3">
                    <Link to={`/villa/${villa.slug || villa.id}`} className="text-sm text-primary">
                      View
                    </Link>
                    <button type="button" onClick={() => editVilla(villa)} className="text-sm text-accent">Edit</button>
                    <button type="button" onClick={() => deleteVilla(villa.id)} disabled={deletingVillaId === villa.id} className="text-sm text-red-600 disabled:opacity-60">
                      {deletingVillaId === villa.id ? 'Deleting...' : 'Delete'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!isLoading && villas.length === 0 ? <p className="text-sm text-textGrey mt-3">No villas yet.</p> : null}
      </div>

      {showPasswordConfirm ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-lg">
            <h3 className="text-lg text-primary font-semibold mb-2">Confirm Deletion</h3>
            <p className="text-sm text-textGrey mb-6">
              To confirm deletion of this villa and all its assets, please enter your login password.
            </p>
            
            <form onSubmit={confirmDeleteWithPassword} className="space-y-4">
              <div>
                <input
                  type="password"
                  placeholder="Enter your password"
                  value={passwordConfirmInput}
                  onChange={(event) => {
                    setPasswordConfirmInput(event.target.value);
                    setPasswordConfirmError('');
                  }}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-accent"
                  disabled={isVerifyingPassword}
                  autoFocus
                />
                {passwordConfirmError ? (
                  <p className="text-xs text-red-600 mt-2">{passwordConfirmError}</p>
                ) : null}
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={cancelPasswordConfirm}
                  disabled={isVerifyingPassword}
                  className="flex-1 px-4 py-3 rounded-xl border border-gray-300 text-sm text-textGrey disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isVerifyingPassword}
                  className="flex-1 px-4 py-3 rounded-xl bg-red-600 text-white text-sm hover:bg-red-700 disabled:opacity-50"
                >
                  {isVerifyingPassword ? 'Verifying...' : 'Delete'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default VillaProjectsAdmin;
